// SP3c — ComposerEditor shell: the tabbed surface (Contenu / Mise en page /
// Approbation) + the single "Enregistrer la composition" button that drives the
// save registry's orchestrated saveAll, with per-tab dirty dots, a browser
// unsaved-changes guard (beforeunload) AND an in-app navigation guard
// (react-router `useBlocker` + kit Dialog).
//
// Panels are injected (contentPanel/layoutPanel/auditPanel) so the shell is
// testable in isolation and decoupled from the (large) editor internals — the
// real panels are wired in by ComposerEditor (SP3c T4/T5).
import { useContext, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Icon } from '@/components/icon';
import { toast } from '@/lib/toast';
import { useComposerSave, type ComposerSaveKey } from './composer-save-registry';

type TabId = 'content' | 'layout' | 'audit';

// Which save keys belong to which tab (drives the per-tab dirty dot).
const TAB_KEYS: Record<TabId, ComposerSaveKey[]> = {
  content: ['programs', 'background_audio'],
  layout: ['layout', 'zone_playlists'],
  audit: [],
};

export interface ComposerEditorShellProps {
  initialTab?: TabId;
  canEdit: boolean;
  contentPanel: ReactNode;
  layoutPanel: ReactNode;
  auditPanel: ReactNode;
}

/**
 * SP3c follow-up — the IN-APP unsaved-changes guard. `useBlocker` intercepts
 * router navigations (sidebar links, back/forward) while `when` is true and
 * holds them in `blocked` state until the operator confirms via the kit
 * Dialog. The beforeunload listener in the shell still covers full page
 * unloads (refresh/close), which useBlocker cannot see.
 *
 * Rendered as a child component because `useBlocker` requires a data router:
 * the shell mounts it only when a data-router context exists, so the shell
 * itself stays mountable standalone (unit tests render it with no router).
 */
function InAppNavigationGuard({ when }: { when: boolean }): JSX.Element | null {
  const { t } = useTranslation(['composers']);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when && currentLocation.pathname !== nextLocation.pathname,
  );

  if (blocker.state !== 'blocked') return null;
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) blocker.reset?.();
      }}
    >
      <DialogContent
        data-testid="composer-unsaved-guard"
        className="max-w-md"
        aria-label={t('composers:unsaved_guard.title', {
          defaultValue: 'Modifications non enregistrées',
        })}
      >
        <DialogHeader>
          <DialogTitle>
            {t('composers:unsaved_guard.title', {
              defaultValue: 'Modifications non enregistrées',
            })}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t('composers:unsaved_guard.body', {
            defaultValue:
              'Cette composition contient des modifications non enregistrées. Quitter sans enregistrer ?',
          })}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => blocker.reset?.()}
            data-testid="composer-unsaved-guard-stay"
          >
            {t('composers:unsaved_guard.stay', { defaultValue: 'Rester' })}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => blocker.proceed?.()}
            data-testid="composer-unsaved-guard-leave"
          >
            {t('composers:unsaved_guard.leave', {
              defaultValue: 'Quitter sans enregistrer',
            })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ComposerEditorShell({
  initialTab = 'content',
  canEdit,
  contentPanel,
  layoutPanel,
  auditPanel,
}: ComposerEditorShellProps): JSX.Element {
  const { t } = useTranslation(['composers', 'common']);
  const { anyDirty, dirtyKeys, saveAll } = useComposerSave();
  const [tab, setTab] = useState<string>(initialTab);
  const [saving, setSaving] = useState(false);

  // In-app navigation guard needs a data router (useBlocker). The production
  // root is one (main.tsx RouterProvider); standalone mounts (tests) are not —
  // there we simply skip the in-app guard and keep beforeunload only.
  const dataRouterCtx = useContext(UNSAFE_DataRouterContext);

  // Warn before browser navigation/refresh/close while there are unsaved edits.
  useEffect(() => {
    if (!anyDirty) return;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [anyDirty]);

  const tabDirty = (id: TabId): boolean => TAB_KEYS[id].some((k) => dirtyKeys.has(k));

  async function handleSave(): Promise<void> {
    setSaving(true);
    try {
      const { ok, failed } = await saveAll();
      if (ok) {
        toast.success(
          t('composers:save_composition_success', { defaultValue: 'Composition enregistrée' }),
        );
      } else {
        toast.error(
          t('composers:save_composition_partial', {
            defaultValue: 'Échec de l’enregistrement : {{parts}}',
            parts: failed.join(', '),
          }),
        );
      }
    } finally {
      setSaving(false);
    }
  }

  const dot = (id: TabId): JSX.Element | null =>
    tabDirty(id) ? (
      <span
        data-testid={`dot-${id}`}
        aria-label={t('composers:tab_unsaved', { defaultValue: 'modifications non enregistrées' })}
        className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-primary align-middle"
      />
    ) : null;

  return (
    <Tabs value={tab} onValueChange={setTab} data-testid="composer-editor">
      {dataRouterCtx != null && <InAppNavigationGuard when={anyDirty} />}
      <div className="flex items-center justify-between gap-3">
        <TabsList>
          <TabsTrigger value="content" data-testid="tab-content" className="gap-1.5">
            <Icon name="film" size={14} aria-hidden />
            {t('composers:tab_content', { defaultValue: 'Contenu' })}
            {dot('content')}
          </TabsTrigger>
          <TabsTrigger value="layout" data-testid="tab-layout" className="gap-1.5">
            <Icon name="grid-3x3" size={14} aria-hidden />
            {t('composers:tab_layout', { defaultValue: 'Mise en page' })}
            {dot('layout')}
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit" className="gap-1.5">
            <Icon name="check-circle" size={14} aria-hidden />
            {t('composers:tab_audit', { defaultValue: 'Approbation' })}
          </TabsTrigger>
        </TabsList>
        <div className="flex items-center gap-3">
          {anyDirty && (
            <span
              data-testid="composer-unsaved-indicator"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground"
            >
              <Icon name="circle-dot" size={14} aria-hidden />
              {t('composers:unsaved_changes', { defaultValue: 'Modifications non enregistrées' })}
            </span>
          )}
          <Button
            onClick={handleSave}
            disabled={!anyDirty || !canEdit || saving}
            data-testid="save-composition"
            className="gap-2"
          >
            <Icon name="check" size={16} aria-hidden />
            {t('composers:save_composition', { defaultValue: 'Enregistrer la composition' })}
          </Button>
        </div>
      </div>
      <TabsContent value="content">{contentPanel}</TabsContent>
      <TabsContent value="layout">{layoutPanel}</TabsContent>
      <TabsContent value="audit">{auditPanel}</TabsContent>
    </Tabs>
  );
}
